import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { storage } from '../firebase';
import { sendMessage } from './messageService';
import { MessageTypes } from '../../models/messaging';
import { validateCareSourceFileSize } from '../../features/careSourceProcessing/textExtraction/careSourceFileTextExtraction';

export const sendChatAttachment = async ({ file, context = {}, retryPayload = null, onPhaseChange = () => {}, deps = {} } = {}) => {
  const upload = deps.uploadBytes || uploadBytes;
  const getUrl = deps.getDownloadURL || getDownloadURL;
  const makeRef = deps.ref || ref;
  const send = deps.sendMessage || sendMessage;
  const validate = deps.validateFile || validateCareSourceFileSize;
  let attachment = retryPayload?.attachment || null;
  const uploadId = retryPayload?.uploadId || `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  const clientMessageId = retryPayload?.clientMessageId || `attachment_${uploadId}`;
  let uploaded = Boolean(retryPayload?.uploaded);
  let path = retryPayload?.path || '';

  if (!attachment) {
    try {
      validate(file);
    } catch (error) {
      return { success: false, error: error.message || 'This file cannot be attached.', retryPayload: null };
    }
    try {
      const isImage = file.type?.startsWith('image/');
      const isVideo = file.type?.startsWith('video/');
      const folder = isImage ? 'images' : isVideo ? 'videos' : 'files';
      path = path || `messages/${context.conversationId}/${folder}/${uploadId}_${file.name}`;
      const fileRef = makeRef(deps.storage || storage, path);
      if (!uploaded) {
        onPhaseChange({ phase: 'uploading', fileName: file.name });
        await upload(fileRef, file);
        uploaded = true;
      } else {
        onPhaseChange({ phase: 'preparing', fileName: file.name });
      }
      const url = await getUrl(fileRef);
      attachment = {
        id: `att_${uploadId}`, type: isImage ? 'image' : 'file', url, filename: file.name, size: file.size,
        mimeType: file.type, path, storagePath: path, metadata: {},
      };
    } catch (error) {
      return { success: false, error: error.message || 'Failed to upload attachment', retryPayload: { file, path, uploaded, uploadId, clientMessageId } };
    }
  }

  const isImage = attachment.type === 'image';
  const isVideo = String(attachment.mimeType || '').startsWith('video/');
  const messageData = {
    conversationId: context.conversationId,
    childId: context.childId,
    senderId: context.currentUserId,
    senderName: context.senderName,
    text: isImage ? 'Photo' : isVideo ? 'Video' : 'File',
    type: MessageTypes.PHOTO,
    category: context.category || null,
    tags: [...(context.tags || [])],
    priority: context.priority,
    replyTo: null,
    attachments: [attachment],
    metadata: { childId: context.childId },
    clientMessageId,
  };
  onPhaseChange({ phase: 'sharing', fileName: attachment.filename || file.name });
  const result = await send(messageData);
  if (!result.success) return { success: false, error: result.error || 'Failed to send attachment', retryPayload: { file, attachment, path: attachment.storagePath, uploaded: true, uploadId, clientMessageId } };
  return {
    success: true,
    sentMessage: {
      id: result.messageId, ...messageData, createdAt: new Date(), updatedAt: new Date(),
      readBy: { [context.currentUserId]: new Date() }, isEdited: false, isDeleted: false,
    },
  };
};
